import {computed, onMounted, onUnmounted, ref} from "vue";
import * as Sentry from "@sentry/vue";
import useSwr from "@/core/composables/swr/useSwr";
import {liveServiceApi, storageService} from "@/container";
import {supportsLiveServiceInstall} from "@/core/services/shellCapabilities";
import {useSystemStore} from "@/stores/system.store";
import {useToastStore} from "@/stores/toast.store";
import {UserSettings} from "@/settings/interfaces";
import {LiveServiceInstallResult, LiveServiceOffer, LiveWatchStatus} from "@/live/interfaces";

/**
 * What the activation offers on this workstation:
 * - `install`: the Apodis Live service is absent;
 * - `upgrade`: the service was put by the Apodis Suite installer and cannot
 *   update itself, so a newer version goes through one more authorization.
 */
export type LiveActivationMode = 'install' | 'upgrade';

/** The offer is read again on this interval, as the shell update's. */
const OFFER_REFRESH_MS = 30 * 60 * 1000;

/** "Plus tard" hides the activation on this workstation for as long. */
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const SNOOZED_UNTIL_KEY = 'liveActivationSnoozedUntil';

/** The shell version the activation dialog was last opened on: once per shell update. */
const PROMPTED_SHELL_KEY = 'liveActivationPromptedShell';

/**
 * In dev, `localStorage.setItem('forceLiveActivation', 'install')` (or
 * `'upgrade'`) offers the activation without the API nor a Windows shell, and
 * `forceLiveActivationResult` ('installed', 'refused' or 'failed') sets what
 * the simulated installation answers.
 */
const forcedMode = (): LiveActivationMode | null => {
    const forced = import.meta.env.DEV ? localStorage.getItem('forceLiveActivation') : null;

    return forced === 'install' || forced === 'upgrade' ? forced : null;
};

const forcedOffer: LiveServiceOffer = {
    target_version: '0.3.3',
    sha256: 'a'.repeat(64),
    offer_install: true,
    lgo_name: 'Winpharma',
};

const forcedStatus = (mode: LiveActivationMode): LiveWatchStatus => ({
    running: false,
    ...(mode === 'install'
        ? {reason: 'service-unavailable', serviceVersion: null, serviceFeatures: []}
        : {serviceVersion: '0.2.8', serviceFeatures: []}),
});

/** Whether version `a` (x.y.z) is newer than `b`. */
function isNewer(a: string, b: string): boolean {
    const left = a.split('.').map(Number);
    const right = b.split('.').map(Number);

    for (let i = 0; i < Math.max(left.length, right.length); i++) {
        const difference = (left[i] || 0) - (right[i] || 0);

        if (difference !== 0) {
            return difference > 0;
        }
    }

    return false;
}

function readItem(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeItem(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Then the activation is simply offered again.
    }
}

/**
 * LIVE — the activation of the automatic patient detection on this
 * workstation (AP-7768): installing the Apodis Live service from Connect,
 * through the Windows authorization prompt, once in the life of the
 * workstation. Later updates go through the service itself, without any
 * authorization.
 *
 * Offered on Windows shells that can install the service, when the API offers
 * it to the pharmacy (its LGO is one the service reads, and the pharmacy is in
 * the opened population). The home offers it unless the pharmacy set the
 * detection to the OCR or the pharmacist chose "Plus tard" in the last 7 days;
 * the settings offer it whenever the pharmacist chooses the rapid detection.
 */
const useLiveActivation = () => {
    const forced = forcedMode();
    const status = ref<LiveWatchStatus | null>(forced ? forcedStatus(forced) : null);
    const ocrForced = ref(false);
    const snoozedUntil = ref(Number(readItem(SNOOZED_UNTIL_KEY) ?? 0));
    const shellVersion = ref<string | null>(null);
    const activating = ref(false);
    const failed = ref(false);

    const supported = forced !== null
        || (supportsLiveServiceInstall() && navigator.userAgent.includes('Windows'));

    const {data: offer} = useSwr(
        () => supported ? 'live-service-offer' : null,
        () => forced ? Promise.resolve(forcedOffer) : liveServiceApi().offer(),
        {ttl: OFFER_REFRESH_MS, refreshInterval: forced ? 0 : OFFER_REFRESH_MS}
    );

    const readStatus = async () => {
        if (forced || !supported) {
            return;
        }

        status.value = await window.ipcRenderer.getLivePatientWatchStatus?.().catch(() => null) ?? null;
    };

    let timer: ReturnType<typeof setInterval> | undefined;

    onMounted(async () => {
        if (!supported) {
            return;
        }

        const settings = await storageService().read('userSettings').catch(() => null) as UserSettings | null;

        ocrForced.value = settings?.detectionMode === 'ocr';
        shellVersion.value = await window.ipcRenderer?.invoke('electron-version').catch(() => null) as string | null ?? 'dev';

        await readStatus();
        timer = setInterval(readStatus, OFFER_REFRESH_MS);
    });

    onUnmounted(() => clearInterval(timer));

    /** What can be installed here, whatever the detection chosen and "Plus tard". */
    const installMode = computed<LiveActivationMode | null>(() => {
        const target = offer.value?.target_version;

        if (!supported || !offer.value?.offer_install || !target || !offer.value.sha256 || !status.value) {
            return null;
        }

        const version = status.value.serviceVersion;

        if (version === null) {
            return 'install';
        }

        if (version && !status.value.serviceFeatures?.includes('update') && isNewer(target, version)) {
            return 'upgrade';
        }

        return null;
    });

    /** What the home offers, regardless of "Plus tard": nothing where the pharmacy chose the OCR. */
    const mode = computed(() => ocrForced.value ? null : installMode.value);

    const isVisible = computed(() => mode.value !== null && Date.now() >= snoozedUntil.value);

    /** The LGO the service reads, as the pharmacist knows it. */
    const lgoName = computed(() => offer.value?.lgo_name || 'votre logiciel de facturation');

    /** Whether the activation is offered and the shell version known: the dialog may then be claimed. */
    const canPrompt = computed(() => isVisible.value && shellVersion.value !== null);

    /**
     * Whether the dialog should open now: the first time the activation is
     * offered on this shell version, that is right after the shell update that
     * brought it. Recorded as soon as asked.
     */
    const claimDialog = (): boolean => {
        if (!canPrompt.value || !shellVersion.value || readItem(PROMPTED_SHELL_KEY) === shellVersion.value) {
            return false;
        }

        writeItem(PROMPTED_SHELL_KEY, shellVersion.value);

        return true;
    };

    /** "Plus tard": hidden on this workstation for 7 days. */
    const snooze = () => {
        snoozedUntil.value = Date.now() + SNOOZE_MS;
        writeItem(SNOOZED_UNTIL_KEY, String(snoozedUntil.value));
    };

    const install = (version: string, sha256: string): Promise<LiveServiceInstallResult> => {
        if (forced) {
            const result = readItem('forceLiveActivationResult');

            return new Promise((resolve) => setTimeout(() => resolve(
                result === 'refused' || result === 'failed'
                    ? {status: result, reason: 'simulated'}
                    : {status: 'installed', version}
            ), 1500));
        }

        return window.ipcRenderer.installLiveService?.(version, sha256)
            ?? Promise.resolve({status: 'failed', reason: 'this shell cannot install the service'});
    };

    /**
     * Install the service, through the Windows authorization prompt. A decline
     * counts as "Plus tard"; a failure goes to Sentry. Once installed, a toast
     * says so and the host registration reports the service's version.
     */
    const activate = async (): Promise<LiveServiceInstallResult> => {
        const from = status.value?.serviceVersion ?? null;
        const {target_version: target, sha256} = offer.value ?? {};

        if (!target || !sha256) {
            return {status: 'failed', reason: 'no version offered'};
        }

        activating.value = true;
        failed.value = false;

        const result: LiveServiceInstallResult = await install(target, sha256)
            .catch((error: unknown) => ({status: 'failed', reason: String(error)}));

        activating.value = false;
        failed.value = result.status === 'failed';

        if (result.status === 'installed') {
            useToastStore().showSuccess({content: "Apodis est à jour."});

            status.value = forced
                ? {running: true, serviceVersion: result.version ?? target, serviceFeatures: ['update']}
                : status.value;
            await readStatus();

            if (!forced) {
                void useSystemStore().updateHostName();
            }
        } else if (result.status === 'refused') {
            snooze();
        } else {
            Sentry.captureMessage('Apodis Live service installation failed', {
                level: 'warning',
                extra: {from, to: target, reason: result.reason},
            });
        }

        return result;
    };

    return {
        mode,
        installMode,
        isVisible,
        lgoName,
        canPrompt,
        claimDialog,
        snooze,
        activating,
        failed,
        activate,
    };
};

export default useLiveActivation;
